import type {
  FollowingContentItem,
  FollowingContentReviewData,
  FollowingContentSaveGroupData,
  FollowingContentShortformData,
  ShortformHashtag,
} from '@tmap-web-lib/remote-api-client/frontman'

import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import dayjs from 'dayjs'
import { useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { storageClickedFollowingContentIdsAtom } from '@/atoms'
import { STORAGE_CLICKED_FOLLOWING_CONTENT_IDS } from '@/atoms/initialState'
import { DialogOk } from '@/components/Dialog'
import { sendEventLog } from '@/features/log/log.fn'
import MyLogDefinition from '@/features/log/log.my'
import { useOpenService } from '@/hooks'
import { useSequentialVideoController } from '@/hooks/useSequentialVideoController'
import { useShortFormVersionCheck } from '@/hooks/useShortFormVersionCheck'
import { useGetRecentFollowingContents } from '@/react-query'
import { pauseVideo, releaseVideo } from '@/utils/video-resource'

import * as styles from './followingContents.css'

interface FollowingContentsProps {
  isRedDot?: boolean
  title?: string
  position?: { lon: number; lat: number } | null
  canAutoplay?: boolean
  resumeKey?: number
  isCard?: boolean
  onReadyRefetch?: (refetch: () => void) => void
}

function FollowingContents({ isRedDot = false, title = '최신 팔로잉 콘텐츠', position = null, canAutoplay = true, resumeKey = 0, isCard = false, onReadyRefetch }: FollowingContentsProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLElement>(null)
  const openService = useOpenService()
  const setClickedIds = useSetAtom(storageClickedFollowingContentIdsAtom)
  const [clickedIds, setLocalClickedIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_CLICKED_FOLLOWING_CONTENT_IDS)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })

  const {
    showUpdateDialog,
    requiredVersion,
    checkVersion,
    closeUpdateDialog,
  } = useShortFormVersionCheck()

  const { data: response, refetch: refetchFollowingContents } = useGetRecentFollowingContents({
    lon: position?.lon ?? 0,
    lat: position?.lat ?? 0,
    enabled: !!position,
  })

  useEffect(() => {
    onReadyRefetch?.(() => refetchFollowingContents())
  }, [onReadyRefetch, refetchFollowingContents])
  const contents = useMemo(() => {
    const allContents = response?.data?.contents ?? []
    return allContents.filter((item) => !clickedIds.includes(item.contentId))
  }, [response?.data?.contents, clickedIds])

  const { action_id } = MyLogDefinition.my

  const handleCardClick = useCallback((item: FollowingContentItem) => {
    if (item.contentType === 'SHORTFORM' && !checkVersion()) return
    sendEventLog(action_id?.tapRecentFollowingContent, {
      content_type: item.contentType === 'SAVE_GROUP' ? 'saved_group' : item.contentType.toLowerCase(),
    })
    setClickedIds((prev: string[]) => prev.includes(item.contentId) ? prev : [...prev, item.contentId])
    setLocalClickedIds((prev) => prev.includes(item.contentId) ? prev : [...prev, item.contentId])
    switch (item.contentType) {
    case 'SHORTFORM': {
      const data = item.data as ShortformCardData
      openService(`tmap://shortform?entry=detail&payload=${encodeURIComponent(JSON.stringify(data.detailParams))}&log=${encodeURIComponent(JSON.stringify({ referrer: 'my' }))}`)
      break
    }
    case 'REVIEW': {
      const data = item.data as FollowingContentReviewData
      TmapApp.showPoiDetailInfo({
        poiId: data.poiId,
        tailParam: {
          tab: 'REVIEW',
          reviewId: data.reviewId,
        },
      })
      break
    }
    case 'SAVE_GROUP': {
      const data = item.data as FollowingContentSaveGroupData
      openService(`tmap://mygroups?pageid=share&extra=${encodeURIComponent(JSON.stringify({ groupid: data.saveGroupId }))}`)
      break
    }
    }
  }, [action_id, setClickedIds, openService, checkVersion])

  const readRedDot = useCallback(() => {
    if (isRedDot) {
      TmapApp.updateRedDotList({
        updateData: [
          {
            itemGroup: 'SERVICE_INDEX',
            item: 'MY_FOLLOWING_CONTENTS',
            displayType: 'REDDOT',
            readDateTime: dayjs().format('YYYYMMDDHHmmss'),
          },
        ],
      })
      TmapApp.updateAllRedDots()
    }
  }, [isRedDot])

  const { bindVideoRef, onInViewChange, onEnded, retryActive } = useSequentialVideoController({
    canAutoplay,
    resumeKey,
  })

  useEffect(() => {
    const container = scrollRef.current
    if (!container) return

    const videos = Array.from(container.querySelectorAll('video'))
    if (videos.length === 0) return

    if (!canAutoplay) {
      videos.forEach((v) => releaseVideo(v))
      return
    }

    videos.forEach((v, i) => bindVideoRef(i, v))

    const playIfInHorizontalView = (video: HTMLVideoElement, index: number) => {
      const containerRect = container.getBoundingClientRect()
      const rect = video.getBoundingClientRect()
      const inView = rect.left < containerRect.right - 64 && rect.right > containerRect.left + 64
      if (!inView) {
        releaseVideo(video)
      }
      onInViewChange(index, inView)
    }

    const nearObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            const target = entry.target as HTMLVideoElement
            requestAnimationFrame(() => releaseVideo(target))
          }
        })
      },
      { root: container, rootMargin: '0px 100% 0px 100%' }
    )

    const activeObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement
          const index = videos.indexOf(video)
          if (entry.isIntersecting) {
            onInViewChange(index, true)
          } else {
            pauseVideo(video)
            onInViewChange(index, false)
          }
        })
      },
      { root: container, rootMargin: '0px -64px 0px -64px' }
    )

    videos.forEach((v) => {
      nearObserver.observe(v)
      activeObserver.observe(v)
      v.addEventListener('ended', () => onEnded(videos.indexOf(v)))
    })

    requestAnimationFrame(() => {
      videos.forEach((v, i) => playIfInHorizontalView(v, i))
    })

    return () => {
      nearObserver.disconnect()
      activeObserver.disconnect()
      videos.forEach((v, i) => {
        releaseVideo(v)
        bindVideoRef(i, null)
      })
    }
  }, [contents, canAutoplay, resumeKey, bindVideoRef, onInViewChange, onEnded, retryActive])

  useEffect(() => {
    const section = sectionRef.current
    const container = scrollRef.current
    if (!section || !container || !canAutoplay) return

    let rafId: number | null = null

    const checkVisibility = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        const rect = section.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
        const ratio = rect.height > 0 ? visibleHeight / rect.height : 0

        const videos = Array.from(container.querySelectorAll<HTMLVideoElement>('video'))
        if (ratio < 0.5) {
          videos.forEach((v) => pauseVideo(v))
        } else {
          const containerRect = container.getBoundingClientRect()
          videos.forEach((v, i) => {
            const vRect = v.getBoundingClientRect()
            const inView = vRect.left < containerRect.right - 64 && vRect.right > containerRect.left + 64
            if (inView) onInViewChange(i, true)
          })
          retryActive()
        }
      })
    }

    const scrollTarget = getScrollParent(section)
    scrollTarget.addEventListener('scroll', checkVisibility, { passive: true })
    if (scrollTarget === window) {
      document.addEventListener('scroll', checkVisibility, { passive: true })
    }
    checkVisibility()
    return () => {
      scrollTarget.removeEventListener('scroll', checkVisibility)
      if (scrollTarget === window) {
        document.removeEventListener('scroll', checkVisibility)
      }
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [contents, canAutoplay, resumeKey, onInViewChange, retryActive])

  const allContents = useMemo(() => response?.data?.contents ?? [], [response?.data?.contents])
  const viewLogSent = useRef(false)

  useEffect(() => {
    if (contents.length === 0 || viewLogSent.current) return
    viewLogSent.current = true
    sendEventLog(action_id?.viewRecentFollowingContent, {
      total_contents_count: allContents.length,
      total_saved_group_count: allContents.filter((item) => item.contentType === 'SAVE_GROUP').length,
      total_review_count: allContents.filter((item) => item.contentType === 'REVIEW').length,
      total_shortform_count: allContents.filter((item) => item.contentType === 'SHORTFORM').length,
    })
  }, [action_id?.viewRecentFollowingContent, contents.length, allContents])

  if (contents.length === 0) return null

  return (
    <>
      <section ref={sectionRef} className={isCard ? styles.sectionCard : styles.section}>
        <p className={`${styles.title} ${isRedDot ? styles.redDot : ''}`}>{title}</p>
        <div className={styles.scrollContainer} ref={scrollRef} onClick={readRedDot}>
          {contents.map((item, idx) => (
            <Card key={idx} item={item} onClick={() => handleCardClick(item)} canAutoplay={canAutoplay} />
          ))}
        </div>
      </section>
      {showUpdateDialog && (
        <DialogOk
          title={`${requiredVersion} 버전 업데이트 후 이용해 주세요.`}
          okText="확인"
          onOk={() => closeUpdateDialog()}
        />
      )}
    </>
  )
}

function Card({ item, onClick, canAutoplay }: { item: FollowingContentItem; onClick: () => void; canAutoplay: boolean }) {
  const author = item.author
  const nickName = author?.nickname ?? '티맵사용자'
  const profileImage = author?.thumbnailUrl ?? ''

  switch (item.contentType) {
  case 'SHORTFORM':
    return <ShortFormCard
      data={item.data as ShortformCardData}
      nickName={nickName}
      profileImage={profileImage}
      onClick={onClick}
      canAutoplay={canAutoplay} />
  case 'REVIEW':
    return <ReviewCard
      data={item.data as FollowingContentReviewData}
      nickName={nickName}
      profileImage={profileImage}
      onClick={onClick}
      canAutoplay={canAutoplay} />
  case 'SAVE_GROUP':
    return <SaveGroupCard
      data={item.data as FollowingContentSaveGroupData}
      nickName={nickName}
      profileImage={profileImage}
      onClick={onClick} />
  default:
    return null
  }
}

function UserBadge({ profileImage, nickName, dark }: { profileImage: string; nickName: string; dark?: boolean }) {
  return (
    <div className={styles.userArea} style={dark ? { background: 'transparent' } : undefined}>
      <img className={styles.profileImage} src={profileImage} alt="" />
      <span className={dark ? styles.nicknameDark : styles.nickname}>{nickName}</span>
    </div>
  )
}

interface CardUserProps {
  nickName: string
  profileImage: string
  onClick: () => void
  canAutoplay?: boolean
}

// TODO: 아래 필드들은 실제 API 응답과 SDK(@tmap-web-lib/remote-api-client)의
// FollowingContentShortformData 타입이 아직 일치하지 않아 임시로 확장/오버라이드 중.
// SDK 타입 업데이트 후 아래 확장 블록 제거 필요.
// [추가 필드 — SDK 미반영]
//   - previewVideoUrl: 프리뷰 비디오 URL
//   - hashtags: 해시태그 목록
//   - poiCount: POI 개수
// [타입 변경 — SDK와 실제 API 불일치]
//   - poiId(string | null) → poiIds(number[]): 단일 POI ID에서 다중 POI ID 배열로 변경
type ShortformCardData = Omit<FollowingContentShortformData, 'poiId'> & {
  previewVideoUrl?: string | null
  hashtags?: ShortformHashtag[] | null
  poiCount?: number | null
  poiIds?: number[] | null
}

function ShortFormCard({ data, nickName, profileImage, onClick, canAutoplay = true }: { data: ShortformCardData } & CardUserProps) {
  const previewUrl = data.previewVideoUrl ?? ''
  const thumbnailUrl = data.thumbnailUrl ?? ''
  const hasPoi = Boolean(data.primaryPoi?.name)
  const poiCount = data.poiCount ?? 0
  const hasMultiPoi = poiCount > 1
  const hashtags = data.hashtags?.map((h) => `#${h.body}`).join(', ') ?? ''
  const hasHashtags = Boolean(hashtags)

  return (
    <div
      className={styles.card}
      onClick={onClick}
      style={thumbnailUrl ? { background: `url(${thumbnailUrl}) center/cover no-repeat` } : undefined}
    >
      {canAutoplay ? (
        <video
          className={styles.cardMedia}
          src={previewUrl}
          data-src={previewUrl}
          poster={thumbnailUrl}
          preload="none"
          muted
          playsInline
        />
      ) : (
        <img className={styles.cardMedia} src={thumbnailUrl} alt="" />
      )}
      <div className={styles.shortFormUserArea}>
        <img className={styles.profileImage} src={profileImage} alt="" />
        <span className={styles.shortFormNickname}>{nickName}</span>
        <img className={styles.shortFormIcon} src="/assets/images/ico_short_form.svg" alt="" />
      </div>
      <div className={styles.shortFormBottomArea}>
        {hasPoi && (
          <div className={styles.shortFormPoi}>
            <span className={styles.shortFormPoiIcon} />
            <span className={styles.shortFormPoiText}>{data.primaryPoi?.name}</span>
            {hasMultiPoi && (
              <span className={styles.shortFormPoiSuffix}>외 {poiCount - 1}</span>
            )}
          </div>
        )}
        {!hasPoi && hasHashtags && (
          <div className={styles.shortFormHashtag}>{hashtags}</div>
        )}
        {data.title && (
          <div className={hasPoi || hasHashtags ? styles.shortFormDescription : styles.shortFormDescriptionLong}>
            {data.title}
          </div>
        )}
      </div>
    </div>
  )
}

function ReviewCard({ data, nickName, profileImage, onClick, canAutoplay = true }: { data: FollowingContentReviewData } & CardUserProps) {
  const mediaThumb = (data.mediaInfos ?? []).map((m) => m?.thumbnailUrls?.[0]).filter(Boolean)[0]
  const previewUrl = (data.mediaInfos ?? []).map((m) => m?.previewUrl).filter(Boolean)[0]
  const hasVideo = Boolean(previewUrl)
  const imageUrl = mediaThumb ?? (data.images ?? []).map((img) => img?.data?.MIDDLE ?? img?.data?.LOW).filter(Boolean)[0] ?? null
  const randomBg = `/assets/images/img_review_random_background_${gradientKeyFromId(data.reviewId) + 1}.svg`

  const hasMedia = hasVideo || Boolean(imageUrl)

  const roundDownNumber = (value: number, decimal: number) => {
    const pow = Math.pow(10, decimal)

    return Math.floor(value * pow) / pow || 0
  }

  const getReviewRate = (value: number) => {
    return roundDownNumber(value, 1).toFixed(1)
  }

  return (
    <div
      className={styles.card}
      onClick={onClick}
      style={
        !hasVideo && imageUrl
          ? { background: `url(${imageUrl}) center/cover no-repeat` }
          : !hasVideo
            ? { background: `url(${randomBg}) center/cover no-repeat` }
            : mediaThumb
              ? { background: `url(${mediaThumb}) center/cover no-repeat` }
              : { background: '#000' }
      }
    >
      {hasVideo && (
        canAutoplay ? (
          <video
            className={styles.cardMedia}
            src={previewUrl!}
            data-src={previewUrl!}
            poster={mediaThumb ?? undefined}
            preload="none"
            muted
            playsInline
          />
        ) : (
          <img className={styles.cardMedia} src={mediaThumb ?? ''} alt="" />
        )
      )}
      <UserBadge profileImage={profileImage} nickName={nickName} />
      <div className={styles.bottomArea} style={!hasMedia ? { background: 'none' } : undefined}>
        <div className={styles.shortFormPoi}>
          <span className={styles.shortFormPoiIcon} />
          <span className={styles.shortFormPoiText}>{data.poiName}</span>
        </div>
        {data.body && <div className={styles.reviewText}>{data.body}</div>}
        <div className={styles.reviewInfo}>
          <div className={styles.reviewInfoItem}>
            <span className={styles.reviewInfoIconStar} />
            <span className={styles.reviewInfoText}>{getReviewRate(data.star / 10)}</span>
          </div>
          <div className={styles.reviewInfoItem}>
            <span className={styles.reviewInfoIconLike} />
            <span className={styles.reviewInfoText}>{data.good1}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function SaveGroupCard({ data, nickName, profileImage, onClick }: { data: FollowingContentSaveGroupData } & CardUserProps) {
  return (
    <div className={styles.cardSaveGroup} onClick={onClick}>
      <div className={styles.saveGroupTop}>
        <div className={styles.saveGroupUser}>
          <img className={styles.profileImage} src={profileImage} alt="" />
          <span className={styles.nicknameDark}>{nickName}</span>
        </div>
        <div className={styles.groupName}>{data.title}</div>
      </div>
      <div className={styles.saveGroupContent}>
        {data.poiFavorites.slice(0, 3).map((poi) => (
          <div key={poi.favId} className={styles.poiItemRow}>
            <span
              className={styles.poiMarker}
              style={{
                backgroundColor: (data.poiGroup as { color?: string } | null)?.color ?? undefined,
                backgroundImage: poi.markerUrl ? `url(${poi.markerUrl})` : 'url(/assets/images/img_marker_default.svg)',
              }}
            />
            <span className={styles.poiItem}>{poi.custName}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export { FollowingContents }

function gradientKeyFromId(id: string) {
  let hash = 0
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  return (hash % 6) as 0 | 1 | 2 | 3 | 4 | 5
}

function getScrollParent(el: HTMLElement): HTMLElement | Window {
  let node: HTMLElement | null = el.parentElement
  while (node) {
    const { overflow, overflowY } = window.getComputedStyle(node)
    if (/auto|scroll/.test(overflow + overflowY)) return node
    node = node.parentElement
  }
  return window
}
